import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { FileText as FileTextIcon, X as XIcon } from 'lucide-react';

type LucideProps = { size?: number; className?: string };
const FileText = FileTextIcon as React.FC<LucideProps>;
const X = XIcon as React.FC<LucideProps>;

import {
  fetchInstanceMetadata,
  fetchSeriesMetadata,
} from '../../../helpers/api/dicom/dicom-metadata';
import {
  fetchAppSeriesMetadata,
  getVideoStreamUrl,
} from '../../../helpers/api/view_frame/view-frames';
import type { GeneralViewerInstance } from '../../../helpers/api/view_frame/view-frames';
import {
  buildAppInstanceMetadataRows,
  buildAppSeriesMetadataRows,
  flattenDicomwebMetadata,
  type DicomMetadataRow,
} from './dicomMetadataTableUtils';

function formatQueryError(err: unknown): string {
  if (err == null) return '';
  if (typeof err === 'string') return err;
  if (typeof err === 'object' && err !== null) {
    const o = err as Record<string, unknown>;
    if (typeof o.detail === 'string') return o.detail;
    if (typeof o.message === 'string') return o.message;
    if (typeof o.error === 'string') return o.error;
  }
  return String(err);
}

/** Series/instance metadata 404 — study not in dcm4chee (video-only, wrong PACS, etc.). */
function isDicomArchiveNotFoundError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const o = err as Record<string, unknown>;
  if (o.status_code === 404) return true;
  const msg = typeof o.message === 'string' ? o.message : '';
  return msg.includes('No DICOM metadata in the archive');
}

function MetadataTable({ rows, filter }: { rows: DicomMetadataRow[]; filter: string }) {
  const { t } = useTranslation('GeneralViewer');
  const q = filter.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!q) return rows;
    return rows.filter((r) => {
      const hay = `${r.tagPunctuated} ${r.tagHex} ${r.name} ${r.vr} ${r.value}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, q]);

  if (filtered.length === 0) {
    return (
      <p className="text-xs text-gray-500 dark:text-gray-400 py-6 text-center">{t('dicomMetadata.empty')}</p>
    );
  }

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <div className="max-h-[min(60vh,520px)] overflow-auto">
        <table className="w-full text-left text-[11px] border-collapse">
          <thead className="sticky top-0 z-10 bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="px-2 py-1.5 font-semibold text-gray-600 dark:text-gray-300 w-[88px]">
                {t('dicomMetadata.colTag')}
              </th>
              <th className="px-2 py-1.5 font-semibold text-gray-600 dark:text-gray-300 min-w-[120px]">
                {t('dicomMetadata.colName')}
              </th>
              <th className="px-2 py-1.5 font-semibold text-gray-600 dark:text-gray-300 w-10">
                {t('dicomMetadata.colVr')}
              </th>
              <th className="px-2 py-1.5 font-semibold text-gray-600 dark:text-gray-300">
                {t('dicomMetadata.colValue')}
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
            {filtered.map((r, rowIdx) => (
              <tr
                key={`${r.tagHex}-${r.name}-${rowIdx}`}
                className="hover:bg-gray-50 dark:hover:bg-gray-800/80"
              >
                <td className="px-2 py-1 font-mono text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {r.tagPunctuated}
                </td>
                <td className="px-2 py-1 text-gray-800 dark:text-gray-200 break-words">
                  {r.name || '—'}
                </td>
                <td className="px-2 py-1 font-mono text-gray-500 dark:text-gray-400">{r.vr}</td>
                <td
                  className="px-2 py-1 font-mono text-gray-800 dark:text-gray-200 break-all max-w-[240px]"
                  title={r.value}
                >
                  {r.value || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Payload for opening the panel from the Exams list / strip (single state object). */
export type DicomMetadataPanelOpenPayload = {
  studyUID: string;
  primarySeriesUid: string | null;
  initialTab: 'study' | 'image';
  selectedInstance: GeneralViewerInstance | null;
};

export interface GeneralViewerDicomMetadataPanelProps {
  open: boolean;
  onClose: () => void;
  studyUID: string;
  /** Series UID for study/series tab (first available series or current instance series). */
  primarySeriesUid: string | null;
  selectedInstance: GeneralViewerInstance | null;
  /** When the panel opens, select this tab (e.g. Image after picking a clip). */
  initialTab?: 'study' | 'image';
}

export default function GeneralViewerDicomMetadataPanel({
  open,
  onClose,
  studyUID,
  primarySeriesUid,
  selectedInstance,
  initialTab = 'study',
}: GeneralViewerDicomMetadataPanelProps) {
  const { t } = useTranslation('GeneralViewer');
  const [tab, setTab] = useState<'study' | 'image'>('study');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    if (open) {
      setTab(initialTab);
      setFilter('');
    }
  }, [open, initialTab]);

  const isVideoInstance = selectedInstance?.format_file === 'VIDEO';

  /** DICOMweb series metadata — only for non-VIDEO instance context (VIDEO lives outside PACS). */
  const seriesQuery = useQuery({
    queryKey: ['general-viewer-dicom-series-meta', studyUID, primarySeriesUid],
    queryFn: () => fetchSeriesMetadata(studyUID, primarySeriesUid!),
    enabled:
      open &&
      tab === 'study' &&
      !!studyUID &&
      !!primarySeriesUid &&
      selectedInstance?.format_file !== 'VIDEO',
    staleTime: 60_000,
    retry: false,
  });

  /** DICOMweb instance metadata — DICOM clips only; VIDEO uses app + stream rows only. */
  const instanceQuery = useQuery({
    queryKey: [
      'general-viewer-dicom-instance-meta',
      studyUID,
      selectedInstance?.serie_instance_uid,
      selectedInstance?.sop_instance_uid,
    ],
    queryFn: () =>
      fetchInstanceMetadata(
        studyUID,
        selectedInstance!.serie_instance_uid,
        selectedInstance!.sop_instance_uid
      ),
    enabled:
      open &&
      tab === 'image' &&
      !!studyUID &&
      !!selectedInstance?.serie_instance_uid &&
      !!selectedInstance?.sop_instance_uid &&
      selectedInstance.format_file !== 'VIDEO',
    staleTime: 60_000,
    retry: false,
  });

  const videoStreamQuery = useQuery({
    queryKey: ['dicom-panel-video-stream', selectedInstance?.sop_instance_uid],
    queryFn: () => getVideoStreamUrl(selectedInstance!.sop_instance_uid),
    enabled:
      open &&
      tab === 'image' &&
      !!selectedInstance?.sop_instance_uid &&
      isVideoInstance,
    staleTime: 60_000,
  });

  const loadAppSeriesStudyMeta =
    open &&
    tab === 'study' &&
    !!studyUID &&
    !!primarySeriesUid &&
    (selectedInstance?.format_file === 'VIDEO' ||
      (seriesQuery.isFetched &&
        seriesQuery.isError &&
        isDicomArchiveNotFoundError(seriesQuery.error)));

  const appSeriesQuery = useQuery({
    queryKey: ['dicom-panel-app-series', studyUID, primarySeriesUid],
    queryFn: () => fetchAppSeriesMetadata(studyUID, primarySeriesUid!),
    enabled: loadAppSeriesStudyMeta,
    staleTime: 60_000,
    retry: false,
  });

  const studyRowsDicom = useMemo(
    () => (seriesQuery.data ? flattenDicomwebMetadata(seriesQuery.data) : []),
    [seriesQuery.data]
  );
  const studyRowsApp = useMemo(
    () => buildAppSeriesMetadataRows(appSeriesQuery.data ?? null),
    [appSeriesQuery.data]
  );
  const studyRowsDisplay =
    studyRowsDicom.length > 0 ? studyRowsDicom : studyRowsApp;

  const dicomImageRows = useMemo(
    () => (instanceQuery.data ? flattenDicomwebMetadata(instanceQuery.data) : []),
    [instanceQuery.data]
  );

  const appImageRows = useMemo(() => {
    if (!selectedInstance || !isVideoInstance) return [];
    return buildAppInstanceMetadataRows(selectedInstance, videoStreamQuery.data ?? null);
  }, [selectedInstance, isVideoInstance, videoStreamQuery.data]);

  const mergedImageRows = useMemo(() => {
    if (!selectedInstance) return [];
    if (isVideoInstance) {
      return appImageRows;
    }
    return dicomImageRows;
  }, [selectedInstance, isVideoInstance, appImageRows, dicomImageRows]);

  if (!open) return null;

  const studyLoadingDicom =
    tab === 'study' &&
    !!primarySeriesUid &&
    selectedInstance?.format_file !== 'VIDEO' &&
    seriesQuery.isLoading;
  const studyLoadingApp = tab === 'study' && loadAppSeriesStudyMeta && appSeriesQuery.isLoading;
  const studyLoading = studyLoadingDicom || studyLoadingApp;

  const showStudyArchiveFallbackFailed =
    tab === 'study' &&
    seriesQuery.isFetched &&
    seriesQuery.isError &&
    isDicomArchiveNotFoundError(seriesQuery.error) &&
    appSeriesQuery.isFetched &&
    appSeriesQuery.isError;

  const showStudyNon404Error =
    tab === 'study' &&
    seriesQuery.isFetched &&
    seriesQuery.isError &&
    !isDicomArchiveNotFoundError(seriesQuery.error);
  const imageLoadingDicom =
    tab === 'image' && selectedInstance && !isVideoInstance && instanceQuery.isLoading;
  const imageLoadingVideoStream =
    tab === 'image' && isVideoInstance && videoStreamQuery.isLoading && !videoStreamQuery.data;

  return (
    <>
      <button
        type="button"
        aria-label={t('dicomMetadata.closeAria')}
        onClick={onClose}
        className="fixed inset-0 z-[100] bg-black/20 dark:bg-black/50 backdrop-blur-[2px]"
      />
      <div className="fixed inset-y-0 right-0 z-[101] w-full max-w-xl flex flex-col bg-white dark:bg-gray-900 border-l border-gray-200 dark:border-gray-800 shadow-2xl overflow-hidden">
        <div className="px-4 py-3.5 flex items-center justify-between gap-2 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FileText size={16} className="text-sky-600 dark:text-sky-400 shrink-0" />
            <span className="text-sm font-bold text-gray-800 dark:text-gray-100 truncate">
              {t('dicomMetadata.panelTitle')}
            </span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-4 pt-3 flex gap-1 border-b border-gray-100 dark:border-gray-800 shrink-0">
          <button
            type="button"
            onClick={() => setTab('study')}
            className={`px-3 py-2 text-xs font-semibold rounded-t-lg transition-colors ${
              tab === 'study'
                ? 'bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 border border-b-0 border-gray-200 dark:border-gray-700'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            {t('dicomMetadata.tabStudy')}
          </button>
          <button
            type="button"
            onClick={() => setTab('image')}
            className={`px-3 py-2 text-xs font-semibold rounded-t-lg transition-colors ${
              tab === 'image'
                ? 'bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300 border border-b-0 border-gray-200 dark:border-gray-700'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            {t('dicomMetadata.tabImage')}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
            {tab === 'study'
              ? studyRowsDicom.length > 0
                ? t('dicomMetadata.studyHelp')
                : loadAppSeriesStudyMeta
                  ? t('dicomMetadata.studyHelpApp')
                  : t('dicomMetadata.studyHelp')
              : isVideoInstance
                ? t('dicomMetadata.imageHelpVideo')
                : t('dicomMetadata.imageHelp')}
          </p>

          <input
            type="search"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder={t('dicomMetadata.searchPlaceholder')}
            className="w-full h-9 px-3 text-xs rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/30"
          />

          {tab === 'study' && !primarySeriesUid && (
            <p className="text-sm text-amber-700 dark:text-amber-400">{t('dicomMetadata.noSeriesContext')}</p>
          )}

          {tab === 'image' && !selectedInstance && (
            <p className="text-sm text-amber-700 dark:text-amber-400">{t('dicomMetadata.noInstance')}</p>
          )}

          {studyLoading && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
              <div className="w-4 h-4 rounded-full border-2 border-gray-200 border-t-sky-500 animate-spin" />
              {t('dicomMetadata.loading')}
            </div>
          )}

          {imageLoadingDicom && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400 py-4">
              <div className="w-4 h-4 rounded-full border-2 border-gray-200 border-t-sky-500 animate-spin" />
              {t('dicomMetadata.loading')}
            </div>
          )}

          {showStudyNon404Error && !studyLoadingDicom && (
            <p className="text-sm text-red-600 dark:text-red-400">
              {t('dicomMetadata.error')}
              {formatQueryError(seriesQuery.error)
                ? ` — ${formatQueryError(seriesQuery.error)}`
                : ''}
            </p>
          )}

          {showStudyArchiveFallbackFailed && (
            <p className="text-sm text-amber-700 dark:text-amber-400">
              {t('dicomMetadata.studyArchiveUnavailable')}
              {formatQueryError(appSeriesQuery.error)
                ? ` — ${formatQueryError(appSeriesQuery.error)}`
                : ''}
            </p>
          )}

          {tab === 'study' &&
            primarySeriesUid &&
            selectedInstance?.format_file === 'VIDEO' &&
            appSeriesQuery.isFetched &&
            appSeriesQuery.isError &&
            !studyLoadingApp && (
              <p className="text-sm text-amber-700 dark:text-amber-400">
                {t('dicomMetadata.studyTabVideoOnly')}
              </p>
            )}

          {!studyLoading &&
            tab === 'study' &&
            primarySeriesUid &&
            studyRowsDisplay.length > 0 && (
              <MetadataTable rows={studyRowsDisplay} filter={filter} />
            )}

          {tab === 'image' && selectedInstance && imageLoadingVideoStream && (
            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 py-2">
              <div className="w-3 h-3 rounded-full border-2 border-gray-200 border-t-sky-500 animate-spin" />
              {t('dicomMetadata.streamInfoLoading')}
            </div>
          )}

          {tab === 'image' &&
            !isVideoInstance &&
            instanceQuery.isError &&
            !instanceQuery.isLoading && (
              <p className="text-sm text-red-600 dark:text-red-400">
                {t('dicomMetadata.error')}
                {formatQueryError(instanceQuery.error)
                  ? ` — ${formatQueryError(instanceQuery.error)}`
                  : ''}
              </p>
            )}

          {tab === 'image' && selectedInstance && !imageLoadingDicom && (
            <>
              {mergedImageRows.length > 0 ? (
                <MetadataTable rows={mergedImageRows} filter={filter} />
              ) : isVideoInstance ? (
                !imageLoadingVideoStream && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 py-4 text-center">
                    {t('dicomMetadata.empty')}
                  </p>
                )
              ) : (
                !instanceQuery.isLoading &&
                !instanceQuery.isError && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 py-4 text-center">
                    {t('dicomMetadata.empty')}
                  </p>
                )
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
